import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import TimeSeriesChart from '../components/TimeSeriesChart';
import Spinner from '../components/Spinner';
import { fetchProfilesReferrals } from '../services/api';
import { createSafeDateFromString } from '../utils/dateUtils';

const NewUserReferralTrends = () => {
    const [monthlyChartData, setMonthlyChartData] = useState(null);
    const [yearlyChartData, setYearlyChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const rawData = await fetchProfilesReferrals();
                
                // Filter data for month_date > 01/01/2020 (only for monthly chart)
                const monthlyFilteredData = rawData.filter(item => {
                    const itemDate = createSafeDateFromString(item.month_date);
                    const cutoffDate = createSafeDateFromString('2020-01-01');
                    return itemDate >= cutoffDate;
                });

                // Use all data for yearly chart (no filtering)
                const yearlyFilteredData = rawData;

                // Group data by sentence and aggregate by month (using filtered data)
                const monthlyGroupedData = {};
                
                monthlyFilteredData.forEach(item => {
                    const sentence = item.sentence;
                    const monthDate = item.month_date;
                    
                    if (!monthlyGroupedData[sentence]) {
                        monthlyGroupedData[sentence] = {};
                    }
                    
                    if (!monthlyGroupedData[sentence][monthDate]) {
                        monthlyGroupedData[sentence][monthDate] = 0;
                    }
                    
                    monthlyGroupedData[sentence][monthDate] += item.user_count;
                });

                // Transform to TimeSeriesChart format for monthly data
                const monthlyChartSeries = Object.entries(monthlyGroupedData).map(([sentence, monthlyData]) => {
                    const seriesData = Object.entries(monthlyData)
                        .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
                        .map(([monthDate, userCount]) => ({
                            x: createSafeDateFromString(monthDate),
                            y: userCount
                        }));

                    return {
                        id: sentence,
                        data: seriesData
                    };
                });

                // Group data by sentence and aggregate by year (using all data)
                const yearlyGroupedData = {};
                
                yearlyFilteredData.forEach(item => {
                    const sentence = item.sentence;
                    const year = createSafeDateFromString(item.month_date).getFullYear().toString();
                    
                    if (!yearlyGroupedData[sentence]) {
                        yearlyGroupedData[sentence] = {};
                    }
                    
                    if (!yearlyGroupedData[sentence][year]) {
                        yearlyGroupedData[sentence][year] = 0;
                    }
                    
                    yearlyGroupedData[sentence][year] += item.user_count;
                });

                // Transform to TimeSeriesChart format for yearly data
                const yearlyChartSeries = Object.entries(yearlyGroupedData).map(([sentence, yearlyData]) => {
                    const seriesData = Object.entries(yearlyData)
                        .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
                        .map(([year, userCount]) => ({
                            x: createSafeDateFromString(`${year}-01-01`), // Convert to Date object
                            y: userCount
                        }));

                    return {
                        id: sentence,
                        data: seriesData
                    };
                });

                // Calculate total counts for each sentence across all data (for consistent sorting)
                const sentenceTotals = {};
                rawData.forEach(item => {
                    if (!sentenceTotals[item.sentence]) {
                        sentenceTotals[item.sentence] = 0;
                    }
                    sentenceTotals[item.sentence] += item.user_count;
                });

                // Sort both charts by overall total count (descending) for consistent legend order
                const sortByOverallCount = (a, b) => {
                    const totalA = sentenceTotals[a.id] || 0;
                    const totalB = sentenceTotals[b.id] || 0;
                    return totalB - totalA;
                };

                monthlyChartSeries.sort(sortByOverallCount);
                yearlyChartSeries.sort(sortByOverallCount);

                setMonthlyChartData(monthlyChartSeries);
                setYearlyChartData(yearlyChartSeries);
            } catch (err) {
                console.error('Error loading referral trends data:', err);
                setError('Failed to load referral trends data');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    if (loading) {
        return (
            <div>
                <Header pageTitle="WtW New User Referral Trends Over Time: By Year and By Month" />
                <div className="flex justify-center items-center" style={{ height: '400px' }}>
                    <Spinner />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <Header pageTitle="WtW New User Referral Trends Over Time: By Year and By Month" />
                <div className="flex justify-center items-center" style={{ height: '400px' }}>
                    <div className="text-red-600 text-lg">{error}</div>
                </div>
            </div>
        );
    }

    if ((!monthlyChartData || monthlyChartData.length === 0) && (!yearlyChartData || yearlyChartData.length === 0)) {
        return (
            <div>
                <Header pageTitle="WtW New User Referral Trends Over Time: By Year and By Month" />
                <div className="flex justify-center items-center" style={{ height: '400px' }}>
                    <div className="text-gray-600 text-lg">No referral data available</div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Header pageTitle="WtW New User Referral Trends Over Time: By Year and By Month" />
            
            {/* Yearly Chart */}
            {yearlyChartData && yearlyChartData.length > 0 && (
                <div className="mb-8">
                    <TimeSeriesChart
                        data={yearlyChartData}
                        title=""
                        xAxisLabel="Year"
                        yAxisLabel="# of Users/Year"
                        height={500}
                        showLegend={true}
                        legendPosition="top-right"
                        dateType="yearly"
                        margin={{ top: 50, right: 150, bottom: 80, left: 80 }}
                        xAxisLegendOffset={90}
                        yAxisLegendOffset={-50}
                    />
                </div>
            )}

            {/* Monthly Chart */}
            {monthlyChartData && monthlyChartData.length > 0 && (
                <div className="mt-8">
                    <TimeSeriesChart
                        data={monthlyChartData}
                        title=""
                        xAxisLabel="Month"
                        yAxisLabel="User Count"
                        height={500}
                        showLegend={true}
                        legendPosition="top-right"
                        dateType="monthly"
                        margin={{ top: 50, right: 150, bottom: 80, left: 80 }}
                        xAxisLegendOffset={90}
                        yAxisLegendOffset={-50}
                    />
                </div>
            )}
        </div>
    );
};

export default NewUserReferralTrends;